import { defineStore } from 'pinia';
import { LOCAL_STORAGE_KEY, PATIENT_ONBOARDING_STATUS } from '@/helpers/enums';
import authService from '@/shared/services/auth.service';
import { useAppStore } from '@/stores/app-store';
import OnboardingSessionManager from '@/helpers/onboarding-session-manager';
import { useFcmSession } from '@/composables/useFcmSession';

const { handleLogin, handleLogout } = useFcmSession();

export const useAuthStore = defineStore('authStore', {
  state: () => ({
    account: {},
    isAuthReady: false,
  }),
  getters: {
    isLoggedIn: (state) => !!(state.account?.email && state.account?.token),
    token: (state) => state.account?.token,
    ME: (state) => state.account,
    patientMainUnique: (state) => state.account?.mainUnique,
    hideMyPrescription: (state) => state.account?.config?.hideMyPrescription || false,
  },
  actions: {
    signIn(account) {
      if (!account?.token) {
        console.error('Invalid account data');
        return;
      }
      if (account.token) {
        localStorage.setItem(LOCAL_STORAGE_KEY.TOKEN, account.token);
      }
      if (account.mainUnique) {
        localStorage.setItem(LOCAL_STORAGE_KEY.PATIENT_MAIN_UNIQUE, account.mainUnique);
      }
      this.account = account;
      handleLogin(account.mainUnique).catch((err) => {
        console.error('FCM subscription failed after sign-in:', err);
      });
    },
    async signOut() {
      // Call handleLogout BEFORE removing token from localStorage
      // so that API requests can still use the token in Authorization header
      try {
        await handleLogout({ rotateToken: true });
      } catch (err) {
        console.error('FCM unsubscription failed after sign-out:', err);
      }
      localStorage.removeItem(LOCAL_STORAGE_KEY.TOKEN);
      localStorage.removeItem(LOCAL_STORAGE_KEY.PATIENT_MAIN_UNIQUE);
      this.account = {};
    },
    async initAuth() {
      this.isAuthReady = false;
      const token = localStorage.getItem(LOCAL_STORAGE_KEY.TOKEN);
      if (!token) {
        this.signOut();
        this.isAuthReady = true;
        return;
      }
      if (this.isLoggedIn) {
        this.isAuthReady = true;
        return;
      }
      this.account = { token };
      try {
        const appStore = useAppStore();
        const user = await authService.getMe();
        appStore.setDateTimeConfig(user?.config?.clientDateTimeFormatConfig);

        if (user?.activeTreatment) {
          OnboardingSessionManager.setActiveTreatment(user.activeTreatment);
        }
        OnboardingSessionManager.setOnboardingStatus(
          user?.onboardingStatus ?? PATIENT_ONBOARDING_STATUS.NOT_YET,
        );

        if (user?.email) {
          this.signIn({ ...user, token });
        } else {
          this.signOut();
        }
      } catch (error) {
        console.error(error);
        this.signOut();
      }
      this.isAuthReady = true;
    },
  },
});
